import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {Observable} from 'rxjs/Observable';
import {filter, flatMap, map, withLatestFrom} from 'rxjs/operators';
import {
    BILD_ID_ENTFERNEN,
    BILD_ID_HINZUFUEGEN, BildIdEntfernenAction,
    BildIdHinzufuegenAction,
    EINGANG_NEU_LADEN,
    EingangNeuLadenAction,
    INCIDENT_AUSGEWAEHLT,
    IncidentAusgewaehltAction,
    LISTE_NEU_LADEN,
    ListeNeuLadenAction, LOAD_PAGE_DATA,
    PageStateResetAction,
    POST_INCIDENT_ABSCHLIESSEN_UI,
    POST_INCIDENT_REAKTIVIEREN_UI,
    POST_INCIDENT_SPEICHERN_UI,
    POST_MELDUNG_ERFASSEN_UI,
    PostIncidentAbschliessenUiAction,
    PostIncidentReaktivierenUiAction,
    PostIncidentSpeichernUiAction,
    PostMeldungErfassenUiAction,
    SETZE_PINNWAND_AKTIV,
    SetzePinnwandAktivAction
} from './incident-leitzentrale.actions.ui';
import {
    GetIncidentBildIdsAction,
    GetIncidentDetailAction, GetZentraleEingang2Action,
    GetZentraleListe2Action,
    GetZentralePinnwand2Action,
    PostIncidentAbschliessenAction,
    PostIncidentAktualisieren2Action,
    PostIncidentBildEntfernenAction,
    PostIncidentBildHinzufuegenAction,
    PostIncidentMeldungAktualisierenAction,
    PostIncidentMeldungErfassenAction,
    PostIncidentPinnwandAktivierenAction,
    PostIncidentPinnwandDeaktivierenAction,
    PostIncidentReaktivierenAction
} from '../../../client-ngrx/incidents';
import {getIncidentDetail, getSelectedMeldung, getSelectedVeranstaltungsIds} from './incident-leitzentrale.selectors';
import {IncidentAktualisieren2Request, IncidentBildRequest, IncidentDetail, IncidentMeldungRequest, IncidentRequest} from '../../../clients/incidents';

import {UuidService} from '@orga-app/providers/uuid.service';


@Injectable()
export class IncidentLeitzentralePageUiEffects {

    @Effect()
    datenLaden$: Observable<Action> = this.actions$.pipe(
        ofType(LOAD_PAGE_DATA),
        withLatestFrom(this.store$.pipe(select(getSelectedVeranstaltungsIds))),
        filter(([x, ids]: [PageStateResetAction, Array<string>]) => (ids !== null && ids !== undefined && ids.length > 0)),
        flatMap(([x, ids]: [PageStateResetAction, Array<string>]) =>
            [new GetZentraleEingang2Action(ids),
            new GetZentraleListe2Action(ids),
            new GetZentralePinnwand2Action(ids)]
        )
    );

    @Effect()
    eingangNeuLaden$: Observable<Action> = this.actions$.pipe(
        ofType<EingangNeuLadenAction>(EINGANG_NEU_LADEN),
        withLatestFrom(this.store$.pipe(select(getSelectedVeranstaltungsIds))),
        filter(([x, ids]) => ids !== null && ids !== undefined && ids.length > 0),
        map(([x, ids]) =>
            new GetZentraleEingang2Action(ids)
        )
    );

    @Effect()
    listeNeuLaden$: Observable<Action> = this.actions$.pipe(
        ofType<ListeNeuLadenAction>(LISTE_NEU_LADEN),
        withLatestFrom(this.store$.pipe(select(getSelectedVeranstaltungsIds))),
        filter(([x, ids]) => ids !== null && ids !== undefined && ids.length > 0),
        map(([x, ids]) =>
            new GetZentraleListe2Action(ids)
        )
    );

   @Effect()
   setzeSelected$: Observable<Action> = this.actions$.pipe(
       ofType<IncidentAusgewaehltAction>(INCIDENT_AUSGEWAEHLT),
       filter((x) => x.payload !== null),
       flatMap(x =>
            [ new GetIncidentDetailAction(x.payload.incident_id),
                new GetIncidentBildIdsAction(x.payload.incident_id)]
       )
   );

    @Effect()
    incidentSpeichern$: Observable<Action> = this.actions$.pipe(
        ofType<PostIncidentSpeichernUiAction>(POST_INCIDENT_SPEICHERN_UI),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]: [PostIncidentSpeichernUiAction, IncidentDetail]) => detail !== null),
        map(([x, detail]: [PostIncidentSpeichernUiAction, IncidentDetail]) => {
            const request: IncidentAktualisieren2Request = {
                incident_id: detail.incident_id,
            };

            request.aktualisieren_request = {
                incident_id: detail.incident_id,
                ist_veranstaltungsrelevant: detail.ist_veranstaltungsrelevant,
                ist_verantwortlichkeit_abteilung: detail.ist_verantwortlichkeit_abteilung,
                ist_verantwortlichkeit_zentrale: detail.ist_verantwortlichkeit_zentrale,
                veranstaltung_id: detail.veranstaltung_id,
                verantwortlichkeit_funktionsbereich_id: detail.verantwortlichkeit_funktionsbereich_id,
            };

            const abteilung = detail.abteilungen.find(abt => abt.abteilung.funktionsbereich_id === detail.verantwortlichkeit_funktionsbereich_id);

            if (detail.ist_verantwortlichkeit_abteilung && abteilung !== null) {
                if (abteilung.abteilung !== null) {
                    if (abteilung.abteilung.zentrale_darf_delegieren) {
                        request.delegation_request = {
                            delegation_funktionsbereich_id: detail.delegation_funktionsbereich_id,
                            delegation_rolle_id: detail.delegation_rolle_id,
                            incident_id: detail.incident_id,
                        };
                    }
                }
            } else if (detail.ist_verantwortlichkeit_zentrale && detail.delegation_rolle_id !== null && detail.delegation_rolle_id !== '') {
                request.delegation_request = {
                    delegation_funktionsbereich_id: detail.delegation_funktionsbereich_id,
                    delegation_rolle_id: detail.delegation_rolle_id,
                    incident_id: detail.incident_id,
                };
            }

            return new PostIncidentAktualisieren2Action(request);
        })
    );

    @Effect()
    incidentAbschliessen$: Observable<Action> = this.actions$.pipe(
        ofType<PostIncidentAbschliessenUiAction>(POST_INCIDENT_ABSCHLIESSEN_UI),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]: [PostIncidentAbschliessenUiAction, IncidentDetail]) => detail !== null),
        map(([x, detail]: [PostIncidentAbschliessenUiAction, IncidentDetail]) => {
                const request: IncidentRequest = {
                   incident_id: detail.incident_id,
                };
                return new PostIncidentAbschliessenAction(request);
            }
        )
    );

    @Effect()
    incidentReaktivieren$: Observable<Action> = this.actions$.pipe(
        ofType<PostIncidentReaktivierenUiAction>(POST_INCIDENT_REAKTIVIEREN_UI),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]) => detail !== null),
        map(([x, detail]) => {
                const request: IncidentRequest = {
                    incident_id: detail.incident_id,
                };
                return new PostIncidentReaktivierenAction(request);
            }
        )
    );

    @Effect()
    meldungErfassen$: Observable<Action> = this.actions$.pipe(
        ofType<PostMeldungErfassenUiAction>(POST_MELDUNG_ERFASSEN_UI),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail)),
            this.store$.pipe(select(getSelectedMeldung))),
        filter(([x, detail, meldung]) => detail !== null && meldung === null),
        map(([x, detail, meldung]) => {
                const request: IncidentMeldungRequest = {
                    incident_id: detail.incident_id,
                    meldung_id: this.uuid.generateUUID(),
                    text: x.payload,
                };
                return new PostIncidentMeldungErfassenAction(request);
            }
        )
    );

    @Effect()
    meldungAendern$: Observable<Action> = this.actions$.pipe(
        ofType<PostMeldungErfassenUiAction>(POST_MELDUNG_ERFASSEN_UI),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail)),
            this.store$.pipe(select(getSelectedMeldung))),
        filter(([x, detail, meldung]) => detail !== null && meldung !== null),
        map(([x, detail, meldung]) => {
                const request: IncidentMeldungRequest = {
                    incident_id: detail.incident_id,
                    meldung_id: meldung.meldung_id,
                    text: x.payload,
                };
                return new PostIncidentMeldungAktualisierenAction(request);
            }
        )
    );

    @Effect()
    pinnwandPost$: Observable<Action> = this.actions$.pipe(
        ofType<SetzePinnwandAktivAction>(SETZE_PINNWAND_AKTIV),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]) => detail !== null),
        map(([x, detail]) => {
                const request: IncidentRequest = {
                    incident_id: detail.incident_id,
                };
                if (x.payload) {
                    return new PostIncidentPinnwandAktivierenAction(request);
                } else {
                    return new PostIncidentPinnwandDeaktivierenAction(request);
                }
            }
        )
    );

    @Effect()
    bildHinzufuegen$: Observable<Action> = this.actions$.pipe(
        ofType<BildIdHinzufuegenAction>(BILD_ID_HINZUFUEGEN),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]) => x.payload !== null && detail !== null),
        map(([x, detail]) => {
            const request: IncidentBildRequest = {
                incident_id: detail.incident_id,
                upload_id: x.payload,
            };
            return new PostIncidentBildHinzufuegenAction(request);
        })
    );

    @Effect()
    bildEntfernen$: Observable<Action> = this.actions$.pipe(
        ofType<BildIdEntfernenAction>(BILD_ID_ENTFERNEN),
        withLatestFrom(this.store$.pipe(select(getIncidentDetail))),
        filter(([x, detail]) => x.payload !== null && detail !== null),
        map(([x, detail]) => {
            const request: IncidentBildRequest = {
                incident_id: detail.incident_id,
                upload_id: x.payload,
            };
            return new PostIncidentBildEntfernenAction(request);
        })
    );

    constructor(private actions$: Actions,
                private uuid: UuidService,
                private store$: Store<any>) {
    }
}
